import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Check, Zap, Shield, TrendingUp, Building2, Clock, CheckCircle, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

const PLANS = [
  {
    id: "basic", name: "Basic", price: 15000, color: "var(--accent-blue)", dim: "var(--accent-blue-dim)",
    features: ["50 tender alerts/month", "Federal Govt sources", "Email + WhatsApp alerts", "Basic AI matching", "1 user"],
  },
  {
    id: "professional", name: "Professional", price: 35000, color: "var(--accent-green)", dim: "var(--accent-green-dim)", popular: true,
    features: ["Unlimited tender alerts", "All 58+ sources", "Advanced AI matching", "Win probability scoring", "Market intelligence", "Priority support", "5 users"],
  },
  {
    id: "enterprise", name: "Enterprise", price: 75000, color: "var(--accent-orange)", dim: "var(--accent-orange-dim)",
    features: ["Everything in Professional", "Bid document generator", "HSE report generator", "Unlimited company profiles", "Multi-company management", "Dedicated account manager", "API access", "Custom integrations", "SLA guarantee", "Unlimited users"],
  },
];



export default function Billing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "SUPER_ADMIN" || user?.role === "ADMIN";
  const [subStatus, setSubStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payingPlan, setPayingPlan] = useState(null);
  const [activeTab, setActiveTab] = useState("plans");
  const [history, setHistory] = useState([]);

  // Always-current mirror of subStatus for the mount-only autocharge effect
  // below, so it can check "already subscribed?" without needing subStatus
  // itself as a dependency (which would re-run that effect on every status
  // refresh instead of once on arrival).
  const subStatusRef = useRef(null);
  useEffect(() => { subStatusRef.current = subStatus; }, [subStatus]);

  useEffect(() => {
    loadStatus();
    if (!document.getElementById("paystack-js")) {
      const s = document.createElement("script");
      s.id = "paystack-js";
      s.src = "https://js.paystack.co/v1/inline.js";
      document.body.appendChild(s);
    }
  }, []);

  const loadStatus = () => {
    const token = localStorage.getItem("access_token");
    if (!token) return;
    fetch(`${API}/payments/status`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(setSubStatus)
      .catch(() => {});
  };

  const handleCardPayment = (plan) => {
    if (!user?.email) { toast.error("No user email found"); return; }
    const token = localStorage.getItem("access_token");

    if (typeof window.PaystackPop === "undefined") {
      toast.error("Payment system loading — please wait a moment and try again.");
      return;
    }

    const reference = `PTN_${plan.id.toUpperCase()}_${Date.now()}`;
    setPayingPlan(plan.id);

    // Validate Paystack public key before proceeding
    const paystackKey = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;
    if (!paystackKey) {
      toast.error("Payment not configured yet. Please contact support@protrackng.com.ng to complete your upgrade.");
      setPayingPlan(null);
      return;
    }

    const handler = window.PaystackPop.setup({
      key: paystackKey,
      email: user.email,
      amount: plan.price * 100,
      currency: "NGN",
      ref: reference,
      metadata: {
        plan_id: plan.id,
        user_id: user.id,
        custom_fields: [{ display_name: "Plan", variable_name: "plan", value: plan.name }],
      },
      callback: (response) => { (async () => {
        setLoading(true);
        try {
          const res = await fetch(`${API}/payments/verify`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ reference: response.reference, plan_id: plan.id }),
          });
          const data = await res.json();
          if (data.success) {
            toast.success(`🎉 Upgraded to ${plan.name}!`);
            loadStatus();
            setHistory(prev => [{ ref: response.reference, plan: plan.name, amount: plan.price, date: new Date().toISOString().slice(0,10), status: "success", method: "card" }, ...prev]);
          } else {
            toast.error(data.detail || "Activation failed — contact support");
          }
        } catch {
          toast.error("Verification error. Contact support with ref: " + response.reference);
        }
        setLoading(false);
        setPayingPlan(null);
      })(); },
      onClose: () => { setPayingPlan(null); setLoading(false); },
    });

    try {
      handler.openIframe();
    } catch (e) {
      toast.error("Could not open payment window. Disable any popup blocker and try again.");
      setPayingPlan(null);
    }
  };

  // Auto-popup for the post-login subscribe-intent flow: Login.js's
  // handleLogin redirects here as "/billing?plan=X&autocharge=true" after a
  // successful login that started from a "Subscribe" link. Same hash-query
  // parsing handleLogin already uses (HashRouter puts the query string after
  // the #, so window.location.hash is the reliable read here, not useLocation).
  const autoTriggeredRef = useRef(false);
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.hash.split("?")[1] || "");
    if (urlParams.get("autocharge") !== "true") return;
    const plan = PLANS.find(p => p.id === urlParams.get("plan"));
    if (!plan) return;

    // Strip the query so a refresh or back-navigation can't re-open checkout.
    navigate("/billing", { replace: true });

    let attempts = 0;
    const tryOpen = () => {
      if (autoTriggeredRef.current) return;
      if (subStatusRef.current?.status === "active") return; // already subscribed — nothing to auto-charge
      if (typeof window.PaystackPop !== "undefined") {
        autoTriggeredRef.current = true;
        handleCardPayment(plan);
        return;
      }
      if (attempts++ < 20) {
        setTimeout(tryOpen, 250);
      } else {
        toast.error("Payment system took a moment too long to load — pick your plan below to continue.");
      }
    };
    tryOpen();
    // Mount-only by design: handleCardPayment/navigate are re-created every
    // render but nothing they close over changes in a way that matters for
    // this one-time arrival check; adding them here would only add noise
    // and risk re-firing the whole autocharge flow on unrelated re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentPlanId = subStatus?.plan;
  const planColor = (id) => PLANS.find(p => p.id === id)?.color || "var(--text-muted)";

  const trialBanner = subStatus?.status === "trial" && (
    <div style={{ padding: "12px 18px", background: "rgba(0,212,161,0.06)", border: "1px solid rgba(0,212,161,0.2)", borderRadius: "12px", marginBottom: "24px", display: "flex", alignItems: "center", gap: "12px" }}>
      <Clock size={16} color="var(--accent-green)" />
      <div>
        <span style={{ fontWeight: "700", color: "var(--accent-green)" }}>Free trial active</span>
        <span style={{ color: "var(--text-secondary)", fontSize: "14px" }}> — {subStatus.days_left} day{subStatus.days_left !== 1 ? "s" : ""} remaining. Subscribe before it ends to keep your access.</span>
      </div>
    </div>
  );

  // Admins have permanent full access — no subscription needed
  if (isAdmin) {
    return (
      <div style={{ maxWidth: "600px" }}>
        <h1 style={{ fontSize: "26px", marginBottom: "8px" }}>Billing & Plans</h1>
        <p style={{ color: "var(--text-secondary)", fontSize: "14px", marginBottom: "24px" }}>Admin account billing</p>
        <div className="card" style={{ borderLeft: "3px solid var(--accent-orange)", background: "rgba(255,140,66,0.05)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
            <div style={{ width: "36px", height: "36px", background: "rgba(255,140,66,0.1)", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Zap size={18} color="var(--accent-orange)" />
            </div>
            <div>
              <div style={{ fontWeight: "800", fontSize: "15px" }}>Super Admin Account</div>
              <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>Permanent full platform access</div>
            </div>
          </div>
          <p style={{ fontSize: "13px", color: "var(--text-secondary)", lineHeight: "1.6" }}>
            Admin accounts are not subject to subscription plans. You have permanent, unrestricted access to all ProTrackNG features.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "980px" }}>
      <style>{`
  @media (max-width: 768px) {
    .mob-grid-2 { grid-template-columns: repeat(2,1fr) !important; }
    .mob-grid-1 { grid-template-columns: 1fr !important; }
    .mob-stack  { grid-template-columns: 1fr !important; }
    .mob-tabs   { overflow-x: auto; -webkit-overflow-scrolling: touch; width: 100% !important; }
    .mob-tabs::-webkit-scrollbar { display: none; }
    .mob-filters { grid-template-columns: 1fr !important; gap: 8px !important; }
  }
`}</style>
      <div style={{ marginBottom: "28px" }}>
        <h1 style={{ fontSize: "26px", marginBottom: "6px" }}>Billing & Subscription</h1>
        <p style={{ color: "var(--text-secondary)", fontSize: "14px" }}>Manage your plan and payment history</p>
      </div>

      {trialBanner}

      {subStatus && subStatus.status !== "expired" && (
        <div style={{ display: "inline-flex", alignItems: "center", gap: "10px", background: "var(--bg-card)", border: `1px solid ${planColor(currentPlanId)}30`, borderRadius: "12px", padding: "10px 18px", marginBottom: "24px" }}>
          <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: planColor(currentPlanId) || "var(--accent-green)" }} />
          <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>CURRENT PLAN</span>
          <span style={{ fontWeight: "800", fontSize: "14px", color: planColor(currentPlanId) || "var(--accent-green)", textTransform: "capitalize" }}>
            {subStatus.status === "trial" ? "Free Trial" : currentPlanId || "—"}
          </span>
          {subStatus.subscription_end && (
            <span style={{ fontSize: "12px", color: "var(--text-muted)", borderLeft: "1px solid var(--border)", paddingLeft: "10px" }}>
              renews {new Date(subStatus.subscription_end).toLocaleDateString("en-GB")}
            </span>
          )}
        </div>
      )}

      <div className="mob-tabs" style={{ display: "flex", gap: "4px", marginBottom: "28px", background: "var(--bg-secondary)", padding: "4px", borderRadius: "12px", width: "fit-content" }}>
        {[{ id: "plans", label: "Plans" }, { id: "history", label: "Payment History" }].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ padding: "8px 20px", borderRadius: "10px", border: "none", background: activeTab === t.id ? "var(--bg-card)" : "transparent", color: activeTab === t.id ? "var(--accent-green)" : "var(--text-secondary)", cursor: "pointer", fontSize: "13px", fontWeight: "600", transition: "all 0.15s" }}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "plans" && (
        <>
          <div className="mob-grid-1" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px", marginBottom: "32px" }}>
            {PLANS.map(plan => {
              const isCurrent = currentPlanId === plan.id && subStatus?.status === "active";
              const isPaying = payingPlan === plan.id;
              return (
                <div key={plan.id} style={{ background: "var(--bg-card)", border: `2px solid ${isCurrent ? plan.color : plan.popular ? plan.color + "30" : "var(--border)"}`, borderRadius: "18px", padding: "28px", position: "relative", transition: "all 0.2s", boxShadow: plan.popular ? `0 8px 32px ${plan.color}10` : "none" }}>
                  {plan.popular && <div style={{ position: "absolute", top: "-12px", left: "50%", transform: "translateX(-50%)", background: plan.color, color: "#0a0f1e", padding: "3px 14px", borderRadius: "100px", fontSize: "10px", fontWeight: "800", whiteSpace: "nowrap" }}>MOST POPULAR</div>}
                  {isCurrent && <div style={{ position: "absolute", top: "16px", right: "16px" }}><CheckCircle size={16} color={plan.color} /></div>}
                  <div style={{ fontSize: "11px", color: plan.color, fontWeight: "800", letterSpacing: "1px", marginBottom: "8px" }}>{plan.name.toUpperCase()}</div>
                  <div style={{ marginBottom: "20px" }}>
                    <span style={{ fontSize: "36px", fontWeight: "900", color: plan.color }}>₦{plan.price / 1000}k</span>
                    <span style={{ fontSize: "13px", color: "var(--text-muted)" }}>/month</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "9px", marginBottom: "24px" }}>
                    {plan.features.map(f => (
                      <div key={f} style={{ display: "flex", gap: "8px" }}>
                        <Check size={13} color={plan.color} style={{ flexShrink: 0, marginTop: "3px" }} />
                        <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>{f}</span>
                      </div>
                    ))}
                  </div>
                  {isCurrent ? (
                    <div style={{ width: "100%", padding: "11px", background: plan.dim, border: `1px solid ${plan.color}40`, borderRadius: "10px", textAlign: "center", fontSize: "13px", fontWeight: "700", color: plan.color }}>✓ Current Plan</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      <button onClick={() => handleCardPayment(plan)} disabled={isPaying || loading} style={{ width: "100%", padding: "11px", background: plan.color, color: "#0a0f1e", border: "none", borderRadius: "10px", fontWeight: "700", fontSize: "13px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                        {isPaying ? <><RefreshCw size={13} style={{ animation: "spin 1s linear infinite" }} /> Processing...</> : <><CreditCard size={13} /> Pay with Card</>}
                      </button>

                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── ELITE — Premium tier ── */}
          {(() => {
            const isCurrent = currentPlanId === "elite" && subStatus?.status === "active";
            const isPaying = payingPlan === "elite";
            return (
              <div style={{ position: "relative", borderRadius: "20px", padding: "32px", background: "linear-gradient(135deg, rgba(167,139,250,0.08), rgba(124,58,237,0.04))", border: isCurrent ? "2px solid #a78bfa" : "1px solid rgba(167,139,250,0.3)", overflow: "hidden", marginBottom: "8px" }}>
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", background: "linear-gradient(90deg, #a78bfa, #7c3aed)" }} />
                {isCurrent && <div style={{ position: "absolute", top: "16px", right: "16px" }}><CheckCircle size={16} color="#a78bfa" /></div>}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "24px" }}>
                  <div style={{ flex: "1 1 300px" }}>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "3px 12px", background: "rgba(167,139,250,0.12)", border: "1px solid rgba(167,139,250,0.3)", borderRadius: "100px", fontSize: "10px", fontWeight: "800", color: "#a78bfa", letterSpacing: "1.5px", marginBottom: "12px" }}>⚡ LEVEL 3 · ELITE PLAN</div>
                    <div style={{ fontSize: "28px", fontWeight: "900", marginBottom: "4px" }}>Elite</div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: "4px", marginBottom: "20px" }}>
                      <span style={{ fontSize: "36px", fontWeight: "900", color: "#a78bfa" }}>₦160k</span>
                      <span style={{ fontSize: "13px", color: "var(--text-muted)" }}>/month</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "8px" }}>
                      {["Everything in Enterprise", "Win Probability Engine (AI)", "Auto Bid Assistant — full docs in seconds", "Competitor Intelligence reports", "Dedicated account manager", "Priority phone support", "Custom API integrations"].map(f => (
                        <div key={f} style={{ display: "flex", gap: "8px", fontSize: "13px", color: "rgba(232,234,240,0.75)" }}>
                          <Check size={13} color="#a78bfa" style={{ flexShrink: 0, marginTop: "3px" }} />{f}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ flex: "0 0 210px", display: "flex", flexDirection: "column", gap: "8px" }}>
                    {isCurrent ? (
                      <div style={{ width: "100%", padding: "12px", background: "rgba(167,139,250,0.12)", border: "1px solid rgba(167,139,250,0.4)", borderRadius: "10px", textAlign: "center", fontSize: "13px", fontWeight: "700", color: "#a78bfa" }}>✓ Current Plan</div>
                    ) : (
                      <>
                        <button onClick={() => handleCardPayment({ id: "elite", name: "Elite", price: 160000, color: "#a78bfa" })} disabled={isPaying || loading} style={{ width: "100%", padding: "12px", background: "linear-gradient(135deg, #a78bfa, #7c3aed)", color: "#fff", border: "none", borderRadius: "10px", fontWeight: "700", fontSize: "13px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                          {isPaying ? <><RefreshCw size={13} style={{ animation: "spin 1s linear infinite" }} /> Processing...</> : <><Zap size={13} /> Pay with Card</>}
                        </button>
                        
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="card" style={{ background: "linear-gradient(135deg, rgba(0,212,161,0.03), rgba(79,142,247,0.02))" }}>
            <h3 style={{ fontSize: "15px", marginBottom: "16px", textAlign: "center" }}>Why contractors choose ProTrackNG</h3>
            <div className="mob-grid-2" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px" }}>
              {[{ icon: Zap, l: "AI-Powered", d: "Bid docs generated instantly" }, { icon: Shield, l: "Compliance", d: "Nigerian regulatory tracking" }, { icon: TrendingUp, l: "Win More", d: "Data-driven intelligence" }, { icon: Building2, l: "Multi-Company", d: "Manage all your entities" }].map(({ icon: Icon, l, d }) => (
                <div key={l} style={{ textAlign: "center" }}>
                  <div style={{ width: "38px", height: "38px", background: "var(--accent-green-dim)", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}><Icon size={18} color="var(--accent-green)" /></div>
                  <div style={{ fontSize: "13px", fontWeight: "600", marginBottom: "3px" }}>{l}</div>
                  <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>{d}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {activeTab === "history" && (
        <div className="card">
          <h3 style={{ fontSize: "16px", marginBottom: "20px" }}>Payment History</h3>
          {history.length === 0 ? (
            <div style={{ textAlign: "center", padding: "48px", color: "var(--text-muted)", fontSize: "13px" }}>No payments yet</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr>{["Reference", "Plan", "Amount", "Date", "Method", "Status"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "10px 12px", color: "var(--text-muted)", fontSize: "11px", letterSpacing: "1px", textTransform: "uppercase", borderBottom: "1px solid var(--border)" }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {history.map(p => (
                  <tr key={p.ref}>
                    <td style={{ padding: "12px", fontFamily: "monospace", fontSize: "11px", color: "var(--text-muted)" }}>{p.ref}</td>
                    <td style={{ padding: "12px", fontWeight: "600" }}>{p.plan}</td>
                    <td style={{ padding: "12px", fontWeight: "600", color: "var(--accent-green)" }}>₦{p.amount.toLocaleString()}</td>
                    <td style={{ padding: "12px", color: "var(--text-secondary)" }}>{p.date}</td>
                    <td style={{ padding: "12px" }}>
                      <span style={{ fontSize: "11px", padding: "3px 10px", borderRadius: "100px", background: p.method === "card" ? "rgba(77,124,255,0.1)" : "rgba(255,140,66,0.1)", color: p.method === "card" ? "var(--accent-blue)" : "var(--accent-orange)", fontWeight: "600" }}>
                        {p.method === "card" ? "💳 Card" : "🏦 Bank"}
                      </span>
                    </td>
                    <td style={{ padding: "12px" }}>
                      <span style={{ fontSize: "11px", padding: "3px 10px", borderRadius: "100px", background: p.status === "success" ? "rgba(0,212,161,0.1)" : "rgba(255,209,102,0.1)", color: p.status === "success" ? "var(--accent-green)" : "#ffd166", fontWeight: "600" }}>
                        {p.status === "success" ? "✓ Paid" : "⏳ Pending"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

    </div>
  );
}